import {
  createApiRef,
  discoveryApiRef,
  useApi,
  ApiRef,
  OAuthApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/core-plugin-api';

export interface CopilotPersona {
  id: string;
  name: string;
  description: string;
  allowedCapabilities?: string[];
  disabledCapabilities?: string[];
  allowedTags?: string[];
  mcpServerIds?: string[];
}

export interface CopilotMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// Mirrors the app-package githubAuthApiRef by ID — resolved at runtime from
// the same Backstage API registry.
export const githubAuthApiRef: ApiRef<
  OAuthApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({ id: 'auth.github' });

export interface CopilotApi {
  getPersonas(): Promise<CopilotPersona[]>;
  chat(
    message: string,
    personaId: string,
    planContext: string | null,
    githubToken: string,
  ): Promise<string>;
}

export const copilotApiRef = createApiRef<CopilotApi>({
  id: 'plugin.workflow-composer.copilot',
});

export class CopilotClient implements CopilotApi {
  private readonly discovery: typeof discoveryApiRef extends ApiRef<infer T>
    ? T
    : never;

  constructor(options: {
    discoveryApi: typeof discoveryApiRef extends ApiRef<infer T> ? T : never;
  }) {
    this.discovery = options.discoveryApi;
  }

  async getPersonas(): Promise<CopilotPersona[]> {
    const baseUrl = await this.discovery.getBaseUrl('copilot');
    const response = await fetch(`${baseUrl}/personas`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.personas ?? [];
  }

  async chat(
    message: string,
    personaId: string,
    planContext: string | null,
    githubToken: string,
  ): Promise<string> {
    const baseUrl = await this.discovery.getBaseUrl('copilot');
    const response = await fetch(`${baseUrl}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        personaId: personaId || undefined,
        githubToken,
        context: planContext ?? undefined,
      }),
    });

    if (!response.ok) {
      throw new Error(`Copilot chat failed: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';
    let accumulated = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'assistant.message_delta') {
            accumulated += event.content;
          } else if (event.type === 'done') {
            accumulated = accumulated || event.content;
          } else if (event.type === 'error') {
            throw new Error(event.error ?? 'Copilot error');
          }
        } catch (e: any) {
          if (e.message?.startsWith('Copilot')) throw e;
          // ignore malformed SSE lines
        }
      }
    }

    return accumulated;
  }
}

// Convenience hook for components
export function useCopilotApi() {
  return useApi(copilotApiRef);
}
